import { QuestionType } from '@/contracts/enums/questionTypes';
import { getQuestionTypeOptions } from '@/features/survey-builder/utils/questionMenuItems';
import { useTranslation } from '@/hooks/useTranslation';
import { Dropdown } from 'primereact/dropdown';
import React, { useId, useMemo } from 'react';

export interface QuestionTypeSelectorProps {
  value: QuestionType;
  onChange: (type: QuestionType) => void;
  disabled?: boolean;
  mixerQuestionId?: string;
  currentQuestionId?: string;
  /** Branch children cannot be Mixer questions */
  excludeMixer?: boolean;
}

export const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  mixerQuestionId,
  currentQuestionId,
  excludeMixer = false,
}) => {
  const { t } = useTranslation();
  const listboxId = useId();

  const questionTypes = getQuestionTypeOptions(
    t,
    mixerQuestionId,
    currentQuestionId,
    excludeMixer
  );

  const ariaLabel = t('editor.fields.questionTypeAriaLabel');

  const dropdownPt = useMemo(
    () => ({
      root: {
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-controls': listboxId,
        'data-testid': 'question-type-selector',
        'data-listbox-id': listboxId,
      },
      panel: {
        role: 'region',
        'aria-label': ariaLabel,
      },
      list: {
        id: listboxId,
      },
      select: {
        'aria-label': ariaLabel,
      },
    }),
    [ariaLabel, listboxId]
  );

  return (
    <Dropdown
      value={value}
      options={questionTypes}
      onChange={e => onChange(e.value)}
      placeholder={t('editor.fields.questionType')}
      disabled={disabled}
      aria-label={ariaLabel}
      className='w-full'
      pt={dropdownPt}
    />
  );
};
